import { motion, Transition, Variants } from "motion/react"

const R1_REST = { x: 125.934, y: 151.139, width: 116.824, height: 20.6922, rx: 10.3461 }
const R2_REST = { x: 85.4727, y: 137.178, width: 197.749, height: 35.0257, rx: 13.1539 }
const R3_REST = { x: 49.9219, y: 102.786, width: 268.85, height: 47.6191, rx: 13.1539 }
const R4_REST = { x: 35.8203, y: 63.396, width: 297.059, height: 52.6155, rx: 13.1539 }

// The new #222222 header bar r4 tucks away behind - matches the <rect> below.
const HEADER_BAR = { y: 0, height: 58 }
// r4's target y: aligned with the header bar's own top, so its full height
// (52.6155) sits well within the bar's bottom edge (58) with margin to
// spare, comfortably hidden even during the bounce's overshoot past this point.
const R4_HIDDEN_Y = HEADER_BAR.y

// All four tiers grow from/rest on the same horizontal center - deriving x
// from width (rather than bouncing x and width as independent numbers) keeps
// that center locked for every frame, including the overshoot keyframe,
// instead of relying on their deltas happening to cancel out.
const CENTER_X =
  (R1_REST.x +
    R1_REST.width / 2 +
    (R2_REST.x + R2_REST.width / 2) +
    (R3_REST.x + R3_REST.width / 2) +
    (R4_REST.x + R4_REST.width / 2)) /
  4
const xFor = (width: number) => CENTER_X - width / 2

// A slight overshoot on the way to each target - much softer than the other
// tool animations' bounce, just enough to feel like a gentle settle.
const OVERSHOOT_RATIO = 0.08
const bounce = (start: number, end: number) => [start, end + (end - start) * OVERSHOOT_RATIO, end]
// Hover (grow in) is slightly slower than rest (shrink back), so the cascade
// reads as a deliberate build-up but snaps back quickly on mouse-out.
const bounceTransition: Transition = { duration: 0.35, ease: "easeOut", times: [0, 0.3, 1] }
const fastBounceTransition: Transition = { duration: 0.23, ease: "easeOut", times: [0, 0.3, 1] }
// Rest has no bounce, but still needs its own explicit transition - without
// one, Motion falls back to independent defaults per property on the way
// back to rest, so x and width drift out of sync and the center wobbles.
const restTransition: Transition = { duration: 0.2, ease: "easeOut" }

function growInto(from: typeof R1_REST, to: typeof R1_REST): Variants {
  const width = bounce(from.width, to.width)
  return {
    rest: {
      x: xFor(from.width),
      y: from.y,
      width: from.width,
      height: from.height,
      rx: from.rx,
      transition: restTransition,
    },
    hover: {
      x: width.map(xFor),
      y: bounce(from.y, to.y),
      width,
      height: bounce(from.height, to.height),
      rx: bounce(from.rx, to.rx),
      transition: bounceTransition,
    },
  }
}

const r1Variants = growInto(R1_REST, R2_REST)
const r2Variants = growInto(R2_REST, R3_REST)
const r3Variants = growInto(R3_REST, R4_REST)

// r4 doesn't fade - it just slides up, a bit quicker than the rest of the
// cascade, and gets covered by the new header bar (painted after it, so it
// paints on top) as it passes underneath.
const r4Variants: Variants = {
  rest: { ...R4_REST, transition: restTransition },
  hover: {
    y: bounce(R4_REST.y, R4_HIDDEN_Y),
    transition: fastBounceTransition,
  },
}

export const DistractionPolicyAnimation = ({ hover }: { hover?: boolean }) => (
  <motion.div
    initial="rest"
    animate={hover ? "hover" : "rest"}
    className="w-full aspect-[41/28] relative [&_svg]:absolute [&_svg]:w-full [&_svg]:h-auto overflow-hidden"
  >
    <svg width="368" height="237" viewBox="0 0 368 237" fill="none" xmlns="http://www.w3.org/2000/svg">
      <motion.rect className="r1" variants={r1Variants} fill="#F3F3F3" />
      <motion.rect className="r2" variants={r2Variants} fill="#FF9344" />
      <motion.rect className="r3" variants={r3Variants} fill="#FFED48" />
      <motion.rect className="r4" variants={r4Variants} fill="#F3F3F3" />
      <rect className="relative z-10" x="0" y="0" width="368" height="58" rx="14.4538" fill="#222222" />
    </svg>
  </motion.div>
)
